import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import usePageTitle from '../../hooks/usePageTitle';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import { useAxios } from '../../core/_axios';
import QRCode from 'react-qr-code';
import { DateTime } from 'luxon';
import { ServiceSelector } from '../../components/ServiceSelector';
import { useToast } from '../../contexts/ToastContext';

interface QrCode {
  unique_identifier: string;
  slug: string;
  link_config: {
    type?: string;
    service_unique_identifier?: string;
  };
  metadata: {
    name?: string;
  };
  is_active: boolean;
  scan_count: number;
  inserted_at: string;
  url: string;
}

interface Service {
  unique_identifier: string;
  public_data: {
    deceased_person: {
      full_name: string;
      avatar_url?: string;
    };
    event_date?: string;
  };
  last_signature_at?: string;
  inserted_at?: string;
}

export function ManageQrCode() {
  const { unique_identifier } = useParams<{ unique_identifier: string }>();
  const axios = useAxios();
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { showSuccess, showError } = useToast();

  const [qrCode, setQrCode] = useState<QrCode | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [baseSlug, setBaseSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedServiceUid, setSelectedServiceUid] = useState('');
  const [qrName, setQrName] = useState('');
  const [isEditingRedirect, setIsEditingRedirect] = useState(false);
  const [customSelectedService, setCustomSelectedService] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'settings'>('details');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);

  usePageTitle('Manage QR Code');

  useEffect(() => {
    setPageHeader('Manage QR Code');
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    fetchQrCodeAndServices();
  }, [unique_identifier]);

  const fetchQrCodeAndServices = async () => {
    try {
      const [qrResponse, servicesResponse, statusResponse] = await Promise.all([
        axios.get(`/api/v1/app/authenticated/current-account/qr-codes/${unique_identifier}`),
        axios.get('/api/v1/app/authenticated/services'),
        axios.get('/api/v1/app/authenticated/current-account/qr-codes/status')
      ]);

      if (qrResponse.data.success) {
        const qr = qrResponse.data.qr_code;
        setQrCode(qr);
        setQrName(qr.metadata?.name || '');
        setSelectedServiceUid(qr.link_config?.service_unique_identifier || '');

        // If already configured, start in read-only mode
        if (qr.link_config?.service_unique_identifier) {
          setIsEditingRedirect(false);
        } else {
          setIsEditingRedirect(true);
        }
      }

      if (statusResponse.data.success && statusResponse.data.funeral_link_base_slug) {
        setBaseSlug(statusResponse.data.funeral_link_base_slug);
      }

      const servicesData = servicesResponse.data.data?.services || servicesResponse.data.services || [];
      // Sort by most recent signature first
      const sortedServices = servicesData.sort((a: Service, b: Service) => {
        if (!a.last_signature_at && !b.last_signature_at) return 0;
        if (!a.last_signature_at) return 1;
        if (!b.last_signature_at) return -1;
        return new Date(b.last_signature_at).getTime() - new Date(a.last_signature_at).getTime();
      });
      setServices(sortedServices);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching QR code:', err);
      setError(err.response?.data?.error || 'Failed to load QR code');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!qrCode) return;

    try {
      setSaving(true);

      const response = await axios.put(
        `/api/v1/app/authenticated/current-account/qr-codes/${unique_identifier}`,
        {
          link_config: selectedServiceUid
            ? { type: 'service', service_unique_identifier: selectedServiceUid }
            : {},
          metadata: {
            name: qrName.trim() || undefined
          }
        }
      );

      if (response.data.success) {
        setQrCode(response.data.qr_code);
        setIsEditingRedirect(false);
        setCustomSelectedService(null);
        showSuccess('QR code updated successfully');
      } else {
        showError(response.data.error || 'Failed to save changes');
      }
    } catch (err: any) {
      console.error('Error saving QR code:', err);
      showError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPNG = () => {
    if (!qrCode) return;

    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;

    // Create a canvas with white background
    const canvas = document.createElement('canvas');
    const size = 512;
    const padding = 32; // White border padding
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, size, size);

    // Convert SVG to image
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // Draw QR code with padding
      const qrSize = size - (padding * 2);
      ctx.drawImage(img, padding, padding, qrSize, qrSize);

      // Convert canvas to PNG and download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const pngUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `funeral-link-${qrCode.slug}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');

      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  const handleArchive = async () => {
    if (!qrCode) return;

    try {
      setArchiving(true);
      const response = await axios.put(
        `/api/v1/app/authenticated/current-account/qr-codes/${unique_identifier}`,
        { is_active: false }
      );

      if (response.data.success) {
        showSuccess('QR code archived successfully');
        navigate('/qr-codes');
      } else {
        showError(response.data.error || 'Failed to archive QR code');
      }
    } catch (err: any) {
      console.error('Error archiving QR code:', err);
      showError(err.response?.data?.error || 'Failed to archive QR code');
    } finally {
      setArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fixed">
        <div className="flex justify-center items-center py-12">
          <div className="spinner spinner-primary"></div>
        </div>
      </div>
    );
  }

  if (error && !qrCode) {
    return (
      <div className="container-fixed">
        <div className="alert alert-error">
          <i className="ki-filled ki-information-2 text-lg"></i>
          <div className="flex flex-col gap-1">
            <div className="font-semibold">Error</div>
            <div className="text-sm">{error}</div>
          </div>
        </div>
        <Link to="/qr-codes" className="btn btn-light mt-4">
          <i className="ki-filled ki-arrow-left"></i>
          Back to QR Codes
        </Link>
      </div>
    );
  }

  if (!qrCode) return null;

  const qrUrl = qrCode.url;

  return (
    <div className="container-fixed max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/qr-codes" className="btn btn-sm btn-light">
          <i className="ki-filled ki-arrow-left"></i>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold">
              {qrCode.metadata?.name || qrCode.slug}
            </h2>
            <div className="flex items-center gap-2">
              <span className="badge badge-lg badge-light-primary">
                <i className="ki-filled ki-barcode text-sm mr-1.5"></i>
                {qrCode.scan_count} {qrCode.scan_count === 1 ? 'scan' : 'scans'}
              </span>
              {!qrCode.is_active && (
                <span className="badge badge-lg badge-light-secondary">Inactive</span>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            <a
              href={qrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {qrUrl.replace('https://', '')}
            </a>
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('details')}
              className={`
                pb-3 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`
                pb-3 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'settings'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Settings
            </button>
          </nav>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Tab Content */}
        {activeTab === 'details' ? (
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Configuration</h3>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Main QR Code"
                value={qrName}
                onChange={(e) => setQrName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Redirect To</label>

              <ServiceSelector
                services={services}
                selectedServiceUid={selectedServiceUid}
                onSelectService={setSelectedServiceUid}
                onCustomServiceSelected={setCustomSelectedService}
                onCustomServiceRemoved={() => {
                  setCustomSelectedService(null);
                  setSelectedServiceUid('');
                }}
                customSelectedService={customSelectedService}
                showNotConfiguredOption={true}
                readOnlyMode={!isEditingRedirect}
                onChangeRedirect={() => setIsEditingRedirect(true)}
              />
            </div>

            <button
              onClick={handleSave}
              className="btn btn-primary w-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner spinner-white spinner-sm"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="ki-filled ki-check"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
        ) : (
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Settings</h3>

            <div className="space-y-6">
              {/* Archive Section */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">Archive QR Code</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Archiving this QR code will hide it from your active QR codes list. The URL will no longer work.
                </p>

                {!showArchiveConfirm ? (
                  <button
                    onClick={() => setShowArchiveConfirm(true)}
                    className="btn btn-danger"
                  >
                    <i className="ki-filled ki-trash"></i>
                    Archive QR Code
                  </button>
                ) : (
                  <div className="bg-danger-light border border-danger rounded-lg p-4">
                    <p className="font-semibold text-sm text-danger mb-3">
                      Are you sure you want to archive this QR code?
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      This action will deactivate the QR code and it will no longer redirect to any service.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleArchive}
                        className="btn btn-danger"
                        disabled={archiving}
                      >
                        {archiving ? (
                          <>
                            <span className="spinner spinner-white spinner-sm"></span>
                            Archiving...
                          </>
                        ) : (
                          <>
                            <i className="ki-filled ki-check"></i>
                            Yes, Archive
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowArchiveConfirm(false)}
                        className="btn btn-light"
                        disabled={archiving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* QR Code Preview & Download */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">QR Code</h3>

          <div className="bg-white p-6 rounded-lg border-2 border-gray-200 mb-4">
            <QRCode
              id="qr-code-svg"
              value={qrUrl}
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox={`0 0 256 256`}
            />
          </div>

          <div className="space-y-3">
            <button
              onClick={handleDownloadPNG}
              className="btn btn-light w-full"
            >
              <i className="ki-filled ki-download"></i>
              Download PNG
            </button>

            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Current URL:</strong></p>
              <a
                href={qrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gray-100 p-2 rounded text-xs break-all text-primary hover:underline"
              >
                {qrUrl}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
